import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, input, Input, Output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CrmTruncatePipe } from '../../shared-pipes/truncate.pipe';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UpdateAssociationFormFieldParams, FormFieldAssociation } from './interface';
import { AssociationFormField, ObjectType } from '../../tokens-and-interfaces';
import {
  AssociationFormModalData,
  CrmAssociationFormModalComponent,
} from './association-form-modal/association-form-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs/operators';

@Component({
  selector: 'crm-association-form-field',
  templateUrl: './association-form-field.component.html',
  styleUrls: ['./association-form-field.component.scss'],
  imports: [
    CommonModule,
    MatIconModule,
    MatChipsModule,
    MatInputModule,
    ReactiveFormsModule,
    TextFieldModule,
    MatAutocompleteModule,
    MatTooltipModule,
    CrmTruncatePipe,
  ],
})
export class CrmAssociationFormFieldComponent {
  useAssociationFormFields = input<boolean>(false);

  private _associations = signal<FormFieldAssociation[]>([]);
  @Input() set associations(associationList: FormFieldAssociation[]) {
    this._associations.set(associationList);
  }
  get associations(): FormFieldAssociation[] {
    return this._associations();
  }

  protected _associationFormFields = signal<AssociationFormField[]>([]);
  @Input() set associationFormFields(associationFormFieldList: AssociationFormField[]) {
    this._associationFormFields.update(() => associationFormFieldList);
  }
  get associationFormFields(): AssociationFormField[] {
    return this._associationFormFields();
  }

  editableAssociations = computed(() => {
    const formFields = this._associationFormFields();
    if (this.useAssociationFormFields()) {
      const editableFormFieldAssociations = [];
      for (const associationFormField of formFields) {
        const curEditableFormFieldAssociations =
          associationFormField.associations?.filter((association) => association?.name !== '') || [];
        editableFormFieldAssociations.push(...curEditableFormFieldAssociations);
      }
      return editableFormFieldAssociations;
    } else {
      return this._associations().filter((association) => association?.name !== '');
    }
  });

  @Input() fixedId: string | null = null;
  @Input({ transform: coerceBooleanProperty }) mobile: boolean | null = null;
  @Input() placeholder = '';
  @Input() currentSearchTermObjectType: ObjectType = 'Contact';
  @Input() currentSearchTermObjectSubtype: string | undefined;
  options = input<FormFieldAssociation[]>([]);
  isMobile = input<boolean>(false);

  @Output() add = new EventEmitter<FormFieldAssociation>();
  @Output() remove = new EventEmitter<FormFieldAssociation>();
  @Output() update = new EventEmitter<AssociationFormField[]>();
  @Output() searchTermChange = new EventEmitter<string | null>();
  @Output() objectType = new EventEmitter<[ObjectType, string?]>();

  protected showAdditionalChips = false;
  protected readonly separatorKeyCodes: number[] = [ENTER, COMMA];

  searchCtrl = new FormControl<string>('');

  constructor(private readonly dialog: MatDialog) {
    this.searchCtrl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((searchTerm) => this.searchTermChange.emit(searchTerm));
  }

  protected handleRemoveOption(option: FormFieldAssociation): void {
    if (this.useAssociationFormFields()) {
      this.removeAssociationFromAssociationFormFields(option);
    } else {
      this.removeAssociation(option);
    }
  }

  protected handleSelectOption(event: MatAutocompleteSelectedEvent): void {
    if (!event.option.value) {
      return;
    }
    this.addAssociation(event.option.value);
    this.searchCtrl.setValue('');
  }

  private removeAssociation(association: FormFieldAssociation) {
    if (association.crmObjectId !== this.fixedId) {
      this.remove.emit(association);
    }
  }

  private removeAssociationFromAssociationFormFields(option: FormFieldAssociation) {
    this.remove.emit(option);
  }

  private addAssociation(association: FormFieldAssociation): void {
    if (!association.crmObjectType || !association.crmObjectId) {
      return;
    }
    if (!this.associations.find((a) => a.crmObjectId === association.crmObjectId)) {
      this.add.emit(association);
    }
  }

  private updateAssociationFormFields(associationParams: UpdateAssociationFormFieldParams): void {
    associationParams.actionEventEmitter.emit(associationParams.associationFormFields);
  }

  protected openAssociationFormModal(): void {
    this.dialog
      .open(CrmAssociationFormModalComponent, {
        width: '500px',
        height: '350px',
        autoFocus: false,
        data: {
          isMobile: this.isMobile,
          initialObjectType: this.currentSearchTermObjectType,
          initialObjectSubtype: this.currentSearchTermObjectSubtype,
          associationFormFields: this._associationFormFields(),
          options: this.options,
          updateAssociation: this.updateAssociationFormFields,
          updateAssociationEventEmitter: this.update,
          searchTermChangeEventEmitter: this.searchTermChange,
          objectTypeEmitter: this.objectType,
        } as AssociationFormModalData,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe();
  }
}
